import {
  Badge,
  Button,
  ColorPaletteEnum,
  getInitials,
  MainContentLayout,
  NotFoundPage,
} from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReactNode, useEffect, useState } from 'react';
import Avatar from 'react-avatar';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { userHasPermission } from '../../../api/firebase/account';
import { getTitlePrefix } from '../../../config/env';
import { toDateObject } from '../../../helpers/dates';
import { paths } from '../../../navigation/paths';
import { studentSelected } from '../../../redux/actions/studentsActions';
import { useSelectedStudent, useStudents } from '../../studentsHooks';
import { isStudentArchived, isStudentEnrolled, isStudentPending, isStudentUnenrolled } from '../../studentsUtils';
import { EnrollDialog } from '../enrollment/EnrollDialog';
import { UnenrollDialog } from '../enrollment/UnenrollDialog';
import StudentDetailProviderApi from '../studentPersonalInformation/StudentDetailProviderApi';
import { StudentEditPageBreadcrumbs } from './StudentEditPageBreadcrumbs';

function StudentEditPageTitle() {
  const { t } = useTranslation();

  return (
    <Helmet>
      <title>
        {getTitlePrefix()}
        {t('student.editPage.pageTitle')}
      </title>
      <body />
    </Helmet>
  );
}

interface StudentEditPageRouteParams {
  studentId?: string;
  [key: string]: string | undefined;
}

export function StudentEditPage() {
  const { studentId } = useParams<StudentEditPageRouteParams>();

  return (
    <>
      <StudentEditPageTitle />
      <MainContentLayout>
        <StudentEditPageBreadcrumbs />
        {studentId && <StudentDetailWrapper key={studentId} studentId={studentId} />}
      </MainContentLayout>
    </>
  );
}

interface StudentDetailWrapperProps {
  studentId: string;
}

function StudentDetailWrapper({ studentId }: Readonly<StudentDetailWrapperProps>) {
  const { list: students } = useStudents();
  const selectedStudent = useSelectedStudent();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const foundStudent = students.find((student) => student.id === studentId);
  const selectedStudentId = selectedStudent?.id;
  const displayName = selectedStudent?.displayName || `${selectedStudent?.firstName} ${selectedStudent?.lastName}`;

  // ensuring there is a selectedStudent in case student page was opened without going through Student List UI
  useEffect(() => {
    if (foundStudent && foundStudent.id !== selectedStudentId) {
      dispatch(studentSelected(foundStudent));
    }
  }, [foundStudent, selectedStudentId, dispatch]);

  return (
    <>
      {students && students.length > 0 && !foundStudent && (
        <NotFoundPage explanation={<span className="text-base">{t('student.editPage.notFoundExplanation')}</span>} />
      )}
      {selectedStudent && (
        <StudentHeader
          studentName={displayName}
          photoUrl={selectedStudent.picture}
          rightSlot={<StudentHeaderActions />}
          isArchived={isStudentArchived(selectedStudent)}
        />
      )}
      {selectedStudent && <StudentDetailProviderApi key={studentId} />}
    </>
  );
}

interface StudentHeaderProps {
  photoUrl?: string;
  studentName: string;
  rightSlot?: ReactNode;
  isArchived?: boolean;
}

function StudentHeader({ photoUrl, studentName, rightSlot, isArchived }: StudentHeaderProps) {
  const { t } = useTranslation();

  // TODO: replace react-avatar with a new clickable avatar component when we can separately upload photos
  return (
    <div className="flex flex-row justify-between">
      <div className="flex items-center gap-4">
        {photoUrl && <Avatar src={photoUrl} color="#346AF4" round />}
        {!photoUrl && <Avatar value={getInitials(studentName)} color="#346AF4" round />}
        <div>
          <h1 className="text-4xl">{studentName}</h1>
          <span className="text-base">{t('students.header.typeLabel')}</span>
          {isArchived && (
            <Badge
              color={ColorPaletteEnum.RED}
              label={t('students.roster.archivedStatus')}
              extraContainerClasses="ml-2"
            />
          )}
        </div>
      </div>
      {rightSlot}
    </div>
  );
}

function StudentHeaderActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedStudent = useSelectedStudent();
  const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);
  const [isUnenrollDialogOpen, setIsUnenrollDialogOpen] = useState(false);
  const { isUnenrollV2Enabled } = useFlags();

  // Gate by permission and status: show if user can edit
  const canEditStudent = userHasPermission('can_edit_student');
  const isUnenrolled = !!selectedStudent && isStudentUnenrolled(selectedStudent);
  const isEnrolled = !!selectedStudent && isStudentEnrolled(selectedStudent);
  const isPending = !!selectedStudent && isStudentPending(selectedStudent);
  const enrollmentDate = toDateObject(selectedStudent?.enrollmentDate);
  const hasEnrollmentDatePassed = enrollmentDate && enrollmentDate <= new Date();
  const canEnroll = isUnenrolled || (isPending && hasEnrollmentDatePassed);

  if (!canEditStudent) return null;

  return (
    <div className="flex items-center">
      {isEnrolled && selectedStudent && (
        <>
          <Button
            onClick={() =>
              isUnenrollV2Enabled
                ? navigate(paths.studentUnenroll.replace(':studentId', selectedStudent.id))
                : setIsUnenrollDialogOpen(true)
            }
            data-testid="unenroll-btn"
            extraClasses="bg-transparent ring-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          >
            {t('enrollments.unenrollStudent')}
          </Button>
          <UnenrollDialog isOpen={isUnenrollDialogOpen} onClose={() => setIsUnenrollDialogOpen(false)} />
        </>
      )}
      {canEnroll && (
        <>
          <Button primary onClick={() => setIsEnrollDialogOpen(true)} data-testid="clear-unenrollment-btn">
            {isPending ? t('enrollments.enrollButton') : t('enrollments.clearUnenrollment')}
          </Button>
          <EnrollDialog
            isOpen={isEnrollDialogOpen}
            onClose={() => setIsEnrollDialogOpen(false)}
            onDone={() => setIsEnrollDialogOpen(false)}
            studentName={selectedStudent.displayName}
            studentEnrollmentStatus={selectedStudent.enrollment.status}
          />
        </>
      )}
    </div>
  );
}
